<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useIncidentStore } from '@/stores/incident'
import { usePersonnelStore } from '@/stores/personnel'
import { useTeamsStore } from '@/stores/teams'
import { useAssignmentsStore } from '@/stores/assignments'
import { TeamType, TeamStatus, AssignmentStatus } from '@/types'

const router = useRouter()
const route = useRoute()

function navigateTo(name: string) {
  router.push({ name, params: { incidentId: route.params.incidentId } })
}

const incidentStore = useIncidentStore()
const personnelStore = usePersonnelStore()
const teamsStore = useTeamsStore()
const assignmentsStore = useAssignmentsStore()

const activeTeams = computed(() =>
  teamsStore.teams.filter(
    (t) => t.team_type !== TeamType.Overhead && t.team_status !== TeamStatus.Returned,
  ),
)

const deployedCount = computed(() =>
  activeTeams.value.filter((t) =>
    [TeamStatus.OnScene, TeamStatus.Enroute, TeamStatus.Assigned].includes(t.team_status),
  ).length,
)

const openAssignmentsCount = computed(
  () =>
    assignmentsStore.assignments.filter((a) => a.assignment_status === AssignmentStatus.InProgress)
      .length,
)

// ── Elapsed timer ─────────────────────────────────────────────────────────────

const elapsedDisplay = ref('—')
let timer: ReturnType<typeof setInterval> | null = null

function getStartMs(): number | null {
  const inc = incidentStore.currentIncident
  if (!inc) return null
  if (inc.start_date) {
    const timeStr = inc.start_time ?? '00:00'
    const dt = new Date(`${inc.start_date}T${timeStr}:00`)
    if (!isNaN(dt.getTime())) return dt.getTime()
  }
  if (inc.created_at) return inc.created_at.toMillis()
  return null
}

function computeElapsed(): string {
  const startMs = getStartMs()
  if (startMs === null) return '—'
  const diffMs = Date.now() - startMs
  const totalMins = Math.floor(diffMs / 60_000)
  const h = Math.floor(totalMins / 60)
  const m = totalMins % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

onMounted(() => {
  elapsedDisplay.value = computeElapsed()
  timer = setInterval(() => {
    elapsedDisplay.value = computeElapsed()
  }, 30_000)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<template>
  <div class="grid grid-cols-4 gap-3">
    <div class="bg-surface border border-border rounded-lg px-4 py-3 cursor-pointer hover:border-primary transition-colors" @click="navigateTo('incident-settings-personnel')">
      <p class="text-[10px] font-label uppercase tracking-widest text-outline mb-1">Personnel on scene</p>
      <p class="text-3xl font-mono font-bold text-primary-container leading-none">
        {{ personnelStore.onSceneCount }}
      </p>
      <p class="text-[10px] text-outline mt-1.5">{{ personnelStore.checkedOutCount }} checked out</p>
    </div>

    <div class="bg-surface border border-border rounded-lg px-4 py-3 cursor-pointer hover:border-primary transition-colors" @click="navigateTo('incident-teams')">
      <p class="text-[10px] font-label uppercase tracking-widest text-outline mb-1">Active teams</p>
      <p class="text-3xl font-mono font-bold text-green-600 leading-none">
        {{ activeTeams.length }}
      </p>
      <p class="text-[10px] text-outline mt-1.5">{{ deployedCount }} in field</p>
    </div>

    <div class="bg-surface border border-border rounded-lg px-4 py-3 cursor-pointer hover:border-primary transition-colors" @click="navigateTo('incident-assignments')">
      <p class="text-[10px] font-label uppercase tracking-widest text-outline mb-1">Open assignments</p>
      <p class="text-3xl font-mono font-bold text-on-surface leading-none">
        {{ openAssignmentsCount }}
      </p>
      <p class="text-[10px] text-outline mt-1.5">in progress</p>
    </div>

    <div class="bg-surface border border-border rounded-lg px-4 py-3">
      <p class="text-[10px] font-label uppercase tracking-widest text-outline mb-1">Incident elapsed</p>
      <p class="text-3xl font-mono font-bold text-on-surface leading-none">{{ elapsedDisplay }}</p>
      <p class="text-[10px] text-outline mt-1.5">hr:min since opened</p>
    </div>
  </div>
</template>
